/**
 * Mock Transaction Repository
 *
 * 로컬 메모리에서 거래 데이터를 관리합니다.
 * 개발 초기 단계 또는 테스트 시 사용합니다.
 */

import type {
  Transaction,
  TransactionInput,
  TransactionFilter,
} from '../types/Transaction'
import type { TransactionRepository } from './TransactionRepository'
import { mockTransactions as initialMockData } from '../mocks/mockTransactions'
import { buildTransaction } from '../utils/buildTransaction'

/**
 * Mock Transaction Repository 구현
 */
export class MockTransactionRepository implements TransactionRepository {
  private transactions: Transaction[]

  constructor() {
    // 초기 데이터는 복사본 사용 (원본 보존)
    this.transactions = [...initialMockData]
  }

  /**
   * 모든 거래 조회 (필터 적용)
   */
  async findAll(filter?: TransactionFilter): Promise<Transaction[]> {
    let result = [...this.transactions]

    if (!filter) {
      return result
    }

    // 날짜 범위 필터
    if (filter.startDate) {
      result = result.filter((tx) => tx.date >= filter.startDate!)
    }
    if (filter.endDate) {
      result = result.filter((tx) => tx.date <= filter.endDate!)
    }

    // 타입 필터
    if (filter.type) {
      result = result.filter((tx) => tx.type === filter.type)
    }

    // 카테고리 필터 (단일 또는 다중 선택)
    if (filter.majorCategoryId) {
      result = result.filter((tx) => tx.majorCategoryId === filter.majorCategoryId)
    }
    if (filter.majorCategoryIds && filter.majorCategoryIds.length > 0) {
      result = result.filter((tx) => filter.majorCategoryIds!.includes(tx.majorCategoryId))
    }
    if (filter.minorCategoryId) {
      result = result.filter((tx) => tx.minorCategoryId === filter.minorCategoryId)
    }
    if (filter.minorCategoryIds && filter.minorCategoryIds.length > 0) {
      result = result.filter((tx) =>
        tx.minorCategoryId && filter.minorCategoryIds!.includes(tx.minorCategoryId)
      )
    }

    // 사용자 필터 (단일 또는 다중 선택)
    if (filter.spenderId) {
      result = result.filter((tx) => tx.spenderId === filter.spenderId)
    }
    if (filter.spenderIds && filter.spenderIds.length > 0) {
      result = result.filter((tx) => filter.spenderIds!.includes(tx.spenderId))
    }
    if (filter.writerId) {
      result = result.filter((tx) => tx.writerId === filter.writerId)
    }
    if (filter.writerIds && filter.writerIds.length > 0) {
      result = result.filter((tx) => filter.writerIds!.includes(tx.writerId))
    }

    // 결제수단 필터 (단일 또는 다중 선택)
    if (filter.paymentMethodId) {
      result = result.filter((tx) => tx.paymentMethodId === filter.paymentMethodId)
    }
    if (filter.paymentMethodIds && filter.paymentMethodIds.length > 0) {
      result = result.filter((tx) => filter.paymentMethodIds!.includes(tx.paymentMethodId))
    }

    // 검색어 필터 (메모)
    if (filter.search) {
      const searchLower = filter.search.toLowerCase()
      result = result.filter((tx) => tx.memo.toLowerCase().includes(searchLower))
    }

    // 날짜 내림차순 정렬 (최신순)
    result.sort((a, b) => b.date.localeCompare(a.date))

    return result
  }

  /**
   * ID로 거래 조회
   */
  async findById(id: string): Promise<Transaction | null> {
    return this.transactions.find((tx) => tx.id === id) || null
  }

  /**
   * 거래 생성
   *
   * @why 다건 경로에 위임해 ID 생성·엔티티 조립 규칙이 단건과 다건에서 갈라지지 않게 한다.
   */
  async create(input: TransactionInput): Promise<Transaction> {
    const [created] = await this.createMany([input])
    return created!
  }

  /**
   * 거래 일괄 생성
   *
   * @why 실제 구현(Google Sheets)이 "한 번에 모두 저장되거나 모두 실패"하므로
   * Mock도 전량을 만든 뒤 한 번에 반영해 같은 시맨틱을 갖게 한다.
   */
  async createMany(inputs: TransactionInput[]): Promise<Transaction[]> {
    if (inputs.length === 0) return []

    const created = inputs.map((input) => buildTransaction(input))

    this.transactions.push(...created)
    return created
  }

  /**
   * 거래 수정
   */
  async update(id: string, input: Partial<TransactionInput>): Promise<Transaction> {
    const index = this.transactions.findIndex((tx) => tx.id === id)

    if (index === -1) {
      throw new Error(`거래를 찾을 수 없습니다: ${id}`)
    }

    const existing = this.transactions[index]
    if (!existing) {
      throw new Error(`거래 데이터를 찾을 수 없습니다: ${id}`)
    }

    const updated: Transaction = {
      ...existing,
      ...input,
      updatedAt: new Date().toISOString(),
    }

    this.transactions[index] = updated
    return updated
  }

  /**
   * 거래 삭제
   */
  async delete(id: string): Promise<void> {
    const index = this.transactions.findIndex((tx) => tx.id === id)

    if (index === -1) {
      throw new Error(`거래를 찾을 수 없습니다: ${id}`)
    }

    this.transactions.splice(index, 1)
  }
}
